import React, { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSwipeable } from 'react-swipeable';
import { Box, useTheme, useMediaQuery, Container } from '@mui/material';
import { ErrorMessage } from '@components/ErrorMessage';
import { LoadingSpinner } from '@components/LoadingSpinner';
import {
  WorkOrderHeader,
  CondensedWorkOrderHeader,
  VoltmeterRack,
  AddEquipmentButton
} from './components';
import { PartsAndChargesView } from '@features/manageWorkOrder';
import { SkipAssetModal } from '@features/calibration/components/shared/SkipAssetModal';
import {
  useWorkOrderDetailData,
  useWorkOrderPartsData,
  useWorkOrderChargesData,
  useSticky
} from './hooks';
import {
  useSkipWorkOrderDetail,
  useIncompleteWorkOrderDetail
} from '@api/enterprise/workorder/mutations';
import { isWorkOrderLocked } from '@utils/workOrderHelpers';
import type { WorkOrderDetailViewModel } from '@api/enterprise/openapi/models';

const WorkOrderProfilePage: React.FC = () => {
  const { jobNumber } = useParams<{ jobNumber: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery('(max-width: 1023px)');
  const [swipeOffset, setSwipeOffset] = useState(0);
  const { isSticky, headerRef } = useSticky();

  const { workOrder, details, isLoading, error } = useWorkOrderDetailData({
    jobNumber: jobNumber || ''
  });

  const { parts, isLoading: isPartsLoading } = useWorkOrderPartsData(
    jobNumber || ''
  );

  const { charges, isLoading: isChargesLoading } = useWorkOrderChargesData(
    jobNumber || ''
  );

  const isReadOnly = useMemo(
    () => isWorkOrderLocked(workOrder?.status),
    [workOrder?.status]
  );

  const [skipModalOpen, setSkipModalOpen] = useState(false);
  const [selectedDetailForSkip, setSelectedDetailForSkip] =
    useState<WorkOrderDetailViewModel | null>(null);

  const skipMutation = useSkipWorkOrderDetail(() => {
    setSkipModalOpen(false);
    setSelectedDetailForSkip(null);
  });

  const incompleteMutation = useIncompleteWorkOrderDetail();

  const handleSkipClick = (detail: WorkOrderDetailViewModel) => {
    setSelectedDetailForSkip(detail);
    setSkipModalOpen(true);
  };

  const handleCloseSkipModal = () => {
    setSkipModalOpen(false);
    setSelectedDetailForSkip(null);
  };

  const handleConfirmSkip = (reason: string) => {
    if (!selectedDetailForSkip || !jobNumber) return;
    skipMutation.mutate({
      jobNumber,
      sequenceNumber: selectedDetailForSkip.sequenceNumber!,
      command: { comments: reason }
    });
  };

  const handleUnskip = (detail: WorkOrderDetailViewModel) => {
    if (!jobNumber) return;
    incompleteMutation.mutate({
      jobNumber,
      sequenceNumber: detail.sequenceNumber!
    });
  };

  const handleDetailClick = (globalId: string) => {
    navigate(
      `/work/${jobNumber}/calibration?instrument=${encodeURIComponent(globalId)}`
    );
  };

  const swipeHandlers = useSwipeable({
    onSwiping: eventData => {
      if (isMobile && eventData.dir === 'Right') {
        setSwipeOffset(Math.min(eventData.deltaX, 100));
      }
    },
    onSwipedRight: () => {
      if (isMobile) {
        setSwipeOffset(0);
        navigate('/work');
      }
    },
    onTouchEndOrOnMouseUp: () => {
      setSwipeOffset(0);
    },
    trackMouse: false,
    delta: 50,
    preventScrollOnSwipe: false,
    trackTouch: true,
    rotationAngle: 0,
    swipeDuration: 500
  });

  // Show loading spinner while data is being fetched
  if (isLoading) {
    return (
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <LoadingSpinner message="Loading work order..." />
      </Container>
    );
  }

  // Handle error state
  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <ErrorMessage
          title="Error loading work order"
          message={error}
          showRetry
          onRetry={() => window.location.reload()}
        />
      </Container>
    );
  }

  // Only show "not found" message after loading is complete and no data exists
  if (!jobNumber || !workOrder) {
    return (
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <ErrorMessage
          title="Work order not found"
          message="The requested work order could not be found."
        />
      </Container>
    );
  }

  return (
    <>
      {isMobile && swipeOffset > 0 && (
        <Box
          sx={{
            position: 'fixed',
            left: 0,
            top: 0,
            height: '100vh',
            width: '3px',
            backgroundColor: theme.palette.primary.main,
            opacity: Math.min(swipeOffset / 50, 1),
            zIndex: 1300,
            transition: 'opacity 0.2s'
          }}
        />
      )}

      <CondensedWorkOrderHeader workOrder={workOrder} visible={isSticky} />

      <Box
        {...(isMobile ? swipeHandlers : {})}
        sx={{
          height: '100%',
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
          transform: isMobile ? `translateX(${swipeOffset}px)` : 'none',
          transition: swipeOffset === 0 ? 'transform 0.3s ease-out' : 'none',
          position: 'relative'
        }}
      >
        <Container
          maxWidth={false}
          sx={{
            maxWidth: isMobile ? '100vw' : '1600px',
            mx: 'auto',
            px: { xs: 2, sm: 2, md: 4 },
            py: isMobile ? 1.5 : 2,
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflowX: 'auto'
          }}
        >
          <Box ref={headerRef}>
            <WorkOrderHeader workOrder={workOrder} details={details} />
          </Box>

          {!isReadOnly && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
              <AddEquipmentButton jobNumber={jobNumber} disabled={isReadOnly} />
            </Box>
          )}

          <VoltmeterRack
            jobNumber={jobNumber || ''}
            details={details}
            isLoading={isLoading}
            isReadOnly={isReadOnly}
            onDetailClick={handleDetailClick}
            onSkipClick={handleSkipClick}
            onUnskipClick={handleUnskip}
          />

          <PartsAndChargesView
            parts={parts}
            charges={charges}
            isPartsLoading={isPartsLoading}
            isChargesLoading={isChargesLoading}
            jobNumber={jobNumber || ''}
            isReadOnly={isReadOnly}
          />
        </Container>
      </Box>

      <SkipAssetModal
        open={skipModalOpen}
        onClose={handleCloseSkipModal}
        onConfirm={handleConfirmSkip}
        isLoading={skipMutation.isPending}
      />
    </>
  );
};

export default WorkOrderProfilePage;
